import { Fragment, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IconBox,
  IconCart,
  IconCheck,
  IconChevronRight,
  IconClock,
  IconDownload,
  IconEdit,
  IconFile,
  IconList,
  IconMail,
  IconTasks,
  IconTrash,
} from '../components/icons';
import { Avatar, Badge, Card, EmptyState, ErrorMessage, Field, Loading, Modal, SearchSelect, Tabs, Toggle, useAsync, useToast } from '../components/ui';
import { api, fetchImageUrl } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  formatCurrency,
  formatDateTime,
  formatDuration,
  formatNumber,
  formatRelative,
  ORDER_STATUS_LABELS,
  SLA_LABELS,
  SLA_VARIANTS,
  STATUS_VARIANTS,
  TIME_KIND_LABELS,
} from '../lib/format';

const ORDER_VARIANTS: Record<string, string> = {
  draft: '',
  pending_approval: 'warning',
  approved: 'primary',
  ordered: 'info',
  partially_received: 'info',
  received: 'success',
  cancelled: 'danger',
};

type TabKey = 'verlauf' | 'kommentare' | 'material' | 'materialbeschaffung' | 'zeiten' | 'checkliste' | 'dokumente';

type OverviewKind = 'status' | 'comment' | 'material' | 'order' | 'time' | 'checklist' | 'document';

type OverviewEntry = {
  key: string;
  ts: string;
  kind: OverviewKind;
  title: string;
  description?: string;
  who: string;
  /** Zielreiter beim Klick; `null` = der Eintrag lebt im Verlauf selbst. */
  tab: TabKey | null;
};

const OVERVIEW_ICON: Record<OverviewKind, ReactNode> = {
  status: <IconCheck width={13} height={13} />,
  comment: <IconMail width={13} height={13} />,
  material: <IconBox width={13} height={13} />,
  order: <IconCart width={13} height={13} />,
  time: <IconClock width={13} height={13} />,
  checklist: <IconList width={13} height={13} />,
  document: <IconFile width={13} height={13} />,
};

function personName(entry: { first_name?: string; last_name?: string }, fallback = 'System'): string {
  const name = `${entry.first_name ?? ''} ${entry.last_name ?? ''}`.trim();
  return name || fallback;
}

function snippet(text: string | null | undefined, max = 140): string {
  const clean = (text ?? '').trim();
  return clean.length > max ? `${clean.slice(0, max)} …` : clean;
}

/**
 * Führt alle Vorkommnisse eines Auftrags zu einer chronologischen Liste
 * zusammen – Statuswechsel, Kommentare, Material, Beschaffung, Zeiten,
 * Checkliste und Dokumente. Neueste zuerst. Jeder Eintrag außer dem
 * Statuswechsel verweist auf den Reiter, der das Element im Detail zeigt.
 */
function buildOverview(data: any, orders: any[], statusLabels: Record<string, string>): OverviewEntry[] {
  const entries: OverviewEntry[] = [];

  for (const h of data.history ?? []) {
    const vorher = h.from_status ? `vorher: ${statusLabels[h.from_status] ?? h.from_status}` : '';
    entries.push({
      key: `status-${h.id}`,
      ts: h.created_at,
      kind: 'status',
      title: `Status: ${statusLabels[h.to_status] ?? h.to_status}`,
      description: [snippet(h.comment), vorher].filter(Boolean).join(' · '),
      who: personName(h),
      tab: null,
    });
  }

  for (const c of data.comments ?? []) {
    entries.push({
      key: `comment-${c.id}`,
      ts: c.created_at,
      kind: 'comment',
      title: c.is_internal ? 'Kommentar (intern)' : 'Kommentar (für Melder sichtbar)',
      description: snippet(c.body),
      who: c.author_label ?? personName(c),
      tab: 'kommentare',
    });
  }

  for (const m of data.materials ?? []) {
    entries.push({
      key: `material-${m.id}`,
      ts: m.created_at,
      kind: 'material',
      title: m.is_return ? 'Material zurückgebucht' : 'Material gebucht',
      description: `${m.is_return ? '−' : ''}${formatNumber(m.quantity, 2)} ${m.unit ?? ''} · ${m.article_name}`.trim(),
      who: personName(m),
      tab: 'material',
    });
  }

  for (const o of orders) {
    entries.push({
      key: `order-${o.id}`,
      ts: o.created_at,
      kind: 'order',
      title: `Materialanforderung ${o.order_no}`,
      description: [o.supplier_name, `${o.item_count} Pos.`, formatCurrency(o.total_net), ORDER_STATUS_LABELS[o.status] ?? o.status]
        .filter(Boolean)
        .join(' · '),
      who: '',
      tab: 'materialbeschaffung',
    });
  }

  for (const t of data.timeEntries ?? []) {
    entries.push({
      key: `time-${t.id}`,
      ts: t.started_at,
      kind: 'time',
      title: `Zeit erfasst: ${formatDuration(t.minutes)}`,
      description: TIME_KIND_LABELS[t.kind] ?? t.kind,
      who: personName(t),
      tab: 'zeiten',
    });
  }

  for (const item of data.checklist ?? []) {
    if (!item.done_at) continue;
    entries.push({
      key: `checklist-${item.id}`,
      ts: item.done_at,
      kind: 'checklist',
      title: 'Checklistenpunkt erledigt',
      description: item.label,
      who: '',
      tab: 'checkliste',
    });
  }

  for (const a of data.attachments ?? []) {
    entries.push({
      key: `document-${a.id}`,
      ts: a.created_at,
      kind: 'document',
      title: 'Dokument hinzugefügt',
      description: [a.filename, a.phase === 'before' ? 'Vorher' : a.phase === 'after' ? 'Nachher' : null]
        .filter(Boolean)
        .join(' · '),
      who: personName(a),
      tab: 'dokumente',
    });
  }

  return entries
    .filter((entry) => entry.ts)
    .sort((a, b) => (a.ts < b.ts ? 1 : a.ts > b.ts ? -1 : 0));
}

export function TaskDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const { notify } = useToast();

  const task = useAsync(() => api.get(`/api/tasks/${id}`), [id]);
  const meta = useAsync(() => api.get('/api/tasks/meta'), []);
  const [tab, setTab] = useState<TabKey>('verlauf');
  const [comment, setComment] = useState('');
  const [descEdit, setDescEdit] = useState(false);
  const [descDraft, setDescDraft] = useState('');
  const [descBusy, setDescBusy] = useState(false);
  const [internal, setInternal] = useState(true);
  const [frage, setFrage] = useState('');
  const [frageOffen, setFrageOffen] = useState(false);
  const [frageBusy, setFrageBusy] = useState(false);
  const [alsAntwort, setAlsAntwort] = useState(false);
  const [statusModal, setStatusModal] = useState<string | null>(null);
  const [assignModal, setAssignModal] = useState(false);
  const [materialModal, setMaterialModal] = useState(false);
  const [orderModal, setOrderModal] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const orders = useAsync(() => api.get('/api/procurement/orders', { taskId: id }), [id]);

  if (task.loading) return <Loading />;
  if (task.error) return <ErrorMessage error={task.error} />;
  if (!task.data) return <EmptyState title="Auftrag nicht gefunden" />;

  const data = task.data;
  /** Offene Rückfragen – steuert das Antwort-Kästchen unter dem Kommentarfeld. */
  const offeneFragen = (data?.comments ?? []).filter((c: any) => c.is_question && !c.answered_at).length;
  const statusLabels: Record<string, string> = Object.fromEntries(
    (meta.data?.statuses ?? []).map((status: any) => [status.key, status.label]),
  );

  const changeStatus = async (status: string, commentText?: string) => {
    try {
      await api.post(`/api/tasks/${id}/status`, { status, comment: commentText });
      notify(`Status geändert: ${statusLabels[status] ?? status}`, 'success');
      setStatusModal(null);
      task.reload();
    } catch (caught) {
      setError(caught);
    }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    await api.post(`/api/tasks/${id}/comments`, {
      body: comment,
      isInternal: internal,
      beantwortetRueckfrage: alsAntwort,
    });
    setComment('');
    setAlsAntwort(false);
    task.reload();
  };

  /**
   * Rückfrage stellen.
   *
   * Adressat und Versand bestimmt der Server – der Ersteller, wenn es ein
   * interner Benutzer ist, sonst die Disposition. Die Oberfläche fragt nur den
   * Text ab und meldet zurück, wo die Frage gelandet ist; alles andere hier zu
   * wiederholen hieße, dieselbe Regel an zwei Orten zu pflegen.
   */
  const stelleRueckfrage = async () => {
    if (!frage.trim()) return;
    setFrageBusy(true);
    try {
      const ergebnis = await api.post(`/api/tasks/${id}/question`, { body: frage });
      setFrage('');
      setFrageOffen(false);
      notify(
        ergebnis.benachrichtigt
          ? `Rückfrage an ${ergebnis.an} gesendet.`
          : `Rückfrage an ${ergebnis.an} vermerkt – es ist keine E-Mail-Adresse hinterlegt.`,
        'success',
      );
      task.reload();
    } catch (caught) {
      setError(caught);
    } finally {
      setFrageBusy(false);
    }
  };

  const startEditDescription = () => {
    setDescDraft(data.description ?? '');
    setDescEdit(true);
    setError(null);
  };

  const saveDescription = async () => {
    setDescBusy(true);
    try {
      await api.patch(`/api/tasks/${id}`, { description: descDraft });
      setDescEdit(false);
      task.reload();
    } catch (caught) {
      setError(caught);
    } finally {
      setDescBusy(false);
    }
  };

  // Der Verlauf bündelt alle Vorkommnisse des Auftrags – Status, Kommentare,
  // Material, Beschaffung, Zeiten, Checkliste und Dokumente – in einer
  // chronologischen Liste. Ein Klick springt in den zuständigen Reiter.
  const overview = buildOverview(data, orders.data?.orders ?? [], statusLabels);

  return (
    <div className="stack">
      <div className="module-header tone-primary">
        <span className="module-icon">
          <IconTasks width={22} height={22} />
        </span>
        <div className="module-text" data-pill="Auftrag">
          <div className="row" style={{ gap: 8 }}>
            <span className="mono subtle">{data.task_no}</span>
            <Badge variant={STATUS_VARIANTS[data.status] ?? ''}>{data.statusLabel}</Badge>
            <Badge variant={SLA_VARIANTS[data.slaState] ?? ''} dot>
              {SLA_LABELS[data.slaState] ?? data.slaState}
            </Badge>
            {data.criticality_name && <Badge color={data.criticality_color}>{data.criticality_name}</Badge>}
          </div>
          <h1 style={{ marginTop: 6 }}>{data.title}</h1>
          <p>
            {data.property_name ?? 'Ohne Objektzuordnung'}
            {data.location_detail ? ` · ${data.location_detail}` : ''} · gemeldet {formatRelative(data.created_at)}
            {data.channel_name ? ` über ${data.channel_name}` : ''}
          </p>
        </div>
        <div className="row">
          <button className="btn" onClick={() => navigate(-1)}>
            Zurück
          </button>
          {can('dispatch.assign') && (
            <button className="btn" onClick={() => setAssignModal(true)}>
              Zuweisen
            </button>
          )}
          {can('tasks.status') &&
            (data.allowedTransitions ?? []).slice(0, 3).map((status: string) =>
              // "Zugewiesen" ist kein bloßer Statuswert: Ihn hier wie jeden
              // anderen Schnellknopf zu setzen, ließe `assignee_id`
              // unangetastet und erzeugte einen Auftrag, der "Zugewiesen"
              // heißt, aber niemandem zugeordnet ist. Der Knopf öffnet daher
              // den echten Zuweisen-Dialog statt des allgemeinen Status-Dialogs
              // – nur der kann `assignee_id` überhaupt setzen.
              status === 'assigned' ? (
                can('dispatch.assign') && (
                  <button key={status} className="btn" onClick={() => setAssignModal(true)}>
                    {statusLabels[status] ?? status}
                  </button>
                )
              ) : (
                <button
                  key={status}
                  className={`btn ${status === 'done' ? 'primary' : ''}`}
                  onClick={() => setStatusModal(status)}
                >
                  {statusLabels[status] ?? status}
                </button>
              ),
            )}
        </div>
      </div>

      <ErrorMessage error={error} />

      <div className="detail-layout">
        <div className="stack">
          <Card
            title="Beschreibung"
            actions={
              can('tasks.update') && !descEdit ? (
                <button className="btn ghost sm" onClick={startEditDescription}>
                  <IconEdit width={14} height={14} /> Bearbeiten
                </button>
              ) : undefined
            }
          >
            {descEdit ? (
              <div className="stack" style={{ gap: 8 }}>
                <textarea
                  value={descDraft}
                  onChange={(event) => setDescDraft(event.target.value)}
                  rows={5}
                  autoFocus
                  placeholder="Beschreibung des Auftrags …"
                />
                <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
                  <button className="btn sm" onClick={() => setDescEdit(false)} disabled={descBusy}>
                    Abbrechen
                  </button>
                  <button className="btn primary sm" onClick={saveDescription} disabled={descBusy}>
                    Speichern
                  </button>
                </div>
              </div>
            ) : (
              <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                {data.description || <span className="subtle">Keine Beschreibung hinterlegt.</span>}
              </p>
            )}
          </Card>

          {data.custom_field_values && Object.keys(data.custom_field_values).length > 0 && (
            <Card title="Zusätzliche Angaben">
              <dl className="definition-list">
                {Object.values(data.custom_field_values as Record<string, { label: string; value: string }>).map(
                  (entry, index) => (
                    <Fragment key={index}>
                      <dt>{entry.label}</dt>
                      <dd style={{ whiteSpace: 'pre-wrap' }}>{entry.value}</dd>
                    </Fragment>
                  ),
                )}
              </dl>
            </Card>
          )}

          <Card noBody>
            <div className="card-body tight" style={{ paddingBottom: 0 }}>
              <Tabs
                active={tab}
                onChange={setTab}
                tabs={[
                  { key: 'verlauf', label: 'Verlauf', count: data.history?.length },
                  { key: 'kommentare', label: 'Kommentare', count: data.comments?.length },
                  { key: 'material', label: 'Material', count: data.materials?.length },
                  { key: 'materialbeschaffung', label: 'Materialbeschaffung', count: orders.data?.orders?.length },
                  { key: 'zeiten', label: 'Zeiten', count: data.timeEntries?.length },
                  { key: 'checkliste', label: 'Checkliste', count: data.checklist?.length },
                  { key: 'dokumente', label: 'Dokumente', count: data.attachments?.length },
                ]}
              />
            </div>

            <div className="card-body">
              {tab === 'verlauf' && (
                <div className="timeline">
                  {overview.length === 0 && <EmptyState title="Noch keine Vorgänge zu diesem Auftrag" />}
                  {overview.map((entry) => {
                    const clickable = entry.tab !== null;
                    const jump = () => entry.tab && setTab(entry.tab);
                    return (
                      <div
                        className={`timeline-item${clickable ? ' clickable' : ''}`}
                        key={entry.key}
                        onClick={clickable ? jump : undefined}
                        role={clickable ? 'button' : undefined}
                        tabIndex={clickable ? 0 : undefined}
                        onKeyDown={
                          clickable
                            ? (event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  jump();
                                }
                              }
                            : undefined
                        }
                      >
                        <div className="timeline-dot">{OVERVIEW_ICON[entry.kind]}</div>
                        <div className="timeline-body">
                          <div className="row between" style={{ gap: 8, flexWrap: 'nowrap' }}>
                            <strong>{entry.title}</strong>
                            {clickable && <IconChevronRight width={14} height={14} className="subtle" />}
                          </div>
                          {entry.description && (
                            <div className="small" style={{ whiteSpace: 'pre-wrap' }}>
                              {entry.description}
                            </div>
                          )}
                          <div className="meta">
                            {entry.who ? `${entry.who} · ` : ''}
                            {formatDateTime(entry.ts)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {tab === 'kommentare' && (
                <div className="stack">
                  {data.replyLinked && (
                    <Badge variant="info">Per Telegram verbunden</Badge>
                  )}
                  {(data.comments ?? []).length === 0 && (
                    <EmptyState title="Noch keine Kommentare" hint="Nutzen Sie Kommentare für Rückfragen." />
                  )}
                  {(data.comments ?? []).map((entry: any) => (
                    <div className={`comment ${entry.is_internal ? 'internal' : ''}`} key={entry.id}>
                      <div className="meta">
                        {entry.author_label ?? `${entry.first_name ?? ''} ${entry.last_name ?? ''}`.trim()} ·{' '}
                        {formatDateTime(entry.created_at)} ·{' '}
                        {entry.is_internal ? 'intern' : entry.author_label ? 'vom Melder' : 'für Melder sichtbar'}
                        {/* Eine Rückfrage ist erst erledigt, wenn jemand
                            geantwortet hat – der Zustand gehört sichtbar an
                            den Eintrag, nicht nur in eine Zählung oben. */}
                        {!!entry.is_question && (
                          <>
                            {' · '}
                            <Badge variant={entry.answered_at ? 'success' : 'warning'}>
                              {entry.answered_at ? 'Rückfrage beantwortet' : 'Rückfrage offen'}
                            </Badge>
                          </>
                        )}
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{entry.body}</div>
                    </div>
                  ))}

                  {can('tasks.comment') && (
                    <div className="stack" style={{ gap: 8 }}>
                      <textarea
                        placeholder="Kommentar hinzufügen …"
                        value={comment}
                        onChange={(event) => setComment(event.target.value)}
                      />
                      <div className="row between">
                        <Toggle
                          checked={internal}
                          onChange={setInternal}
                          label={
                            data.replyLinked
                              ? 'Interner Kommentar (sonst per Telegram an den Melder gesendet)'
                              : 'Interner Kommentar (nicht für den Melder sichtbar)'
                          }
                        />
                        <button className="btn primary sm" onClick={addComment} disabled={!comment.trim()}>
                          Kommentieren
                        </button>
                      </div>
                      {/* Nur zeigen, wenn es etwas zu beantworten gibt – sonst
                          steht dort eine Frage ohne Frage. */}
                      {offeneFragen > 0 && (
                        <Toggle
                          checked={alsAntwort}
                          onChange={setAlsAntwort}
                          label={`Als Antwort auf die offene Rückfrage kennzeichnen (${offeneFragen})`}
                        />
                      )}
                    </div>
                  )}

                  {/* Rückfrage stellen – getrennt vom Kommentar, weil sie
                      einen Adressaten hat und den Auftrag anhält. */}
                  {can('tasks.comment') && (
                    <div className="stack" style={{ gap: 8, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                      {frageOffen ? (
                        <>
                          <textarea
                            placeholder="Was ist unklar? Die Frage geht an den Ersteller, sonst an die Disposition."
                            value={frage}
                            onChange={(event) => setFrage(event.target.value)}
                            autoFocus
                          />
                          <div className="row">
                            <button
                              className="btn primary sm"
                              onClick={stelleRueckfrage}
                              disabled={!frage.trim() || frageBusy}
                            >
                              {frageBusy ? 'Wird gesendet …' : 'Rückfrage senden'}
                            </button>
                            <button className="btn sm" onClick={() => setFrageOffen(false)} disabled={frageBusy}>
                              Abbrechen
                            </button>
                          </div>
                        </>
                      ) : (
                        <button className="btn sm" onClick={() => setFrageOffen(true)}>
                          Rückfrage stellen
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {tab === 'material' && (
                <div className="stack">
                  {(data.materials ?? []).length === 0 ? (
                    <EmptyState title="Kein Material gebucht" />
                  ) : (
                    <div className="table-wrap">
                      <table className="data">
                        <thead>
                          <tr>
                            <th>Artikel</th>
                            <th>Lagerort</th>
                            <th className="num">Menge</th>
                            <th className="num">Einzelpreis</th>
                            <th className="num">Summe</th>
                            <th>Gebucht von</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.materials.map((booking: any) => (
                            <tr key={booking.id}>
                              <td>
                                {booking.article_name}
                                <div className="small subtle mono">{booking.article_no}</div>
                              </td>
                              <td className="small">{booking.warehouse_name ?? '–'}</td>
                              <td className="num">
                                {booking.is_return ? '−' : ''}
                                {formatNumber(booking.quantity, 2)} {booking.unit}
                              </td>
                              <td className="num">{formatCurrency(booking.unit_price)}</td>
                              <td className="num">
                                {formatCurrency(booking.quantity * booking.unit_price * (booking.is_return ? -1 : 1))}
                              </td>
                              <td className="small">
                                {booking.first_name} {booking.last_name}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {can('material.book') && (
                    <div>
                      <button className="btn sm" onClick={() => setMaterialModal(true)}>
                        Material buchen
                      </button>
                    </div>
                  )}
                </div>
              )}

              {tab === 'materialbeschaffung' && (
                <div className="stack">
                  {orders.loading ? (
                    <Loading />
                  ) : !(orders.data?.orders ?? []).length ? (
                    <EmptyState title="Keine Materialanforderung zu diesem Auftrag" />
                  ) : (
                    <div className="table-wrap">
                      <table className="data">
                        <thead>
                          <tr>
                            <th>Nummer</th>
                            <th>Lieferant</th>
                            <th className="num">Positionen</th>
                            <th className="num">Wert</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders.data.orders.map((order: any) => (
                            <tr key={order.id}>
                              <td className="mono small">{order.order_no}</td>
                              <td className="small">{order.supplier_name ?? '–'}</td>
                              <td className="num">{order.item_count}</td>
                              <td className="num">{formatCurrency(order.total_net)}</td>
                              <td>
                                <Badge variant={ORDER_VARIANTS[order.status] ?? ''}>
                                  {ORDER_STATUS_LABELS[order.status] ?? order.status}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {can('procurement.request') && (
                    <div>
                      <button className="btn sm" onClick={() => setOrderModal(true)}>
                        Anforderung stellen
                      </button>
                    </div>
                  )}
                </div>
              )}

              {tab === 'zeiten' && (
                <>
                  {(data.timeEntries ?? []).length === 0 ? (
                    <EmptyState title="Keine Zeiten erfasst" />
                  ) : (
                    <div className="table-wrap">
                      <table className="data">
                        <thead>
                          <tr>
                            <th>Mitarbeiter</th>
                            <th>Art</th>
                            <th>Beginn</th>
                            <th className="num">Dauer</th>
                            <th>Freigabe</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.timeEntries.map((entry: any) => (
                            <tr key={entry.id}>
                              <td className="small">
                                {entry.first_name} {entry.last_name}
                              </td>
                              <td>
                                <Badge variant={entry.kind === 'travel' ? 'info' : ''}>
                                  {TIME_KIND_LABELS[entry.kind] ?? entry.kind}
                                </Badge>
                              </td>
                              <td className="small">{formatDateTime(entry.started_at)}</td>
                              <td className="num">{formatDuration(entry.minutes)}</td>
                              <td>
                                <Badge variant={entry.approval_status === 'approved' ? 'success' : entry.approval_status === 'rejected' ? 'danger' : ''}>
                                  {approvalLabel(entry.approval_status)}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {tab === 'checkliste' && (
                <div className="stack" style={{ gap: 8 }}>
                  {(data.checklist ?? []).length === 0 ? (
                    <EmptyState title="Keine Checkliste zugeordnet" hint="Checklisten werden je Gewerk in der Administration gepflegt." />
                  ) : (
                    data.checklist.map((item: any) => (
                      <label className="checkbox-row" key={item.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                        <input
                          type="checkbox"
                          checked={!!item.is_done}
                          disabled={!can('tasks.status')}
                          onChange={async (event) => {
                            await api.patch(`/api/tasks/${id}/checklist/${item.id}`, { isDone: event.target.checked });
                            task.reload();
                          }}
                        />
                        <span style={{ flex: 1 }}>
                          {item.label}
                          {item.is_required ? <span style={{ color: 'var(--danger)' }}> *</span> : null}
                        </span>
                        {item.done_at && <span className="small subtle">{formatDateTime(item.done_at)}</span>}
                      </label>
                    ))
                  )}
                </div>
              )}

              {tab === 'dokumente' && (
                <div className="stack">
                  {(data.attachments ?? []).length === 0 ? (
                    <EmptyState title="Keine Dokumente" hint="Fotos werden in der Regel über die mobile App erfasst." />
                  ) : (
                    <div className="grid cols-4">
                      {data.attachments.map((attachment: any) => (
                        <div className="card" key={attachment.id}>
                          <div className="card-body tight">
                            <strong className="small truncate">{attachment.filename}</strong>
                            <div className="small subtle">
                              {attachment.phase === 'before' ? 'Vorher' : attachment.phase === 'after' ? 'Nachher' : attachment.kind}
                              {' · '}
                              {formatDateTime(attachment.created_at)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {data.signature_data && (
                    <div className="alert success">
                      Unterschrift vor Ort bestätigt durch <strong>{data.signature_name}</strong> am{' '}
                      {formatDateTime(data.signature_at)}.
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>

        <div className="stack">
          <Card title="Übersicht">
            <dl className="definition-list">
              <dt>Zuständig</dt>
              <dd>
                {data.assignee_first_name ? (
                  <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                    <Avatar firstName={data.assignee_first_name} lastName={data.assignee_last_name} size="sm" />
                    {data.assignee_first_name} {data.assignee_last_name}
                  </div>
                ) : (
                  <span className="subtle">nicht zugewiesen</span>
                )}
              </dd>

              <dt>Melder</dt>
              <dd>
                {data.reporter_user_first_name
                  ? `${data.reporter_user_first_name} ${data.reporter_user_last_name}`
                  : [data.reporter_first_name, data.reporter_name].filter(Boolean).join(' ') || '–'}
              </dd>

              {!data.reporter_user_first_name && (data.reporter_phone || data.reporter_email || data.reporter_contact) && (
                <>
                  <dt>Kontakt (Melder)</dt>
                  <dd>
                    {[data.reporter_phone, data.reporter_email].filter(Boolean).join(' · ') || data.reporter_contact}
                  </dd>
                </>
              )}

              <dt>Eigentümer/Verwalter</dt>
              <dd>{data.customer_name ?? '–'}</dd>

              <dt>Kategorie</dt>
              <dd>{data.category_name ?? '–'}</dd>

              <dt>Kanal</dt>
              <dd>{data.channel_name ?? '–'}</dd>

              <dt>Reaktion bis</dt>
              <dd>{data.respond_by ? formatDateTime(data.respond_by) : '–'}</dd>

              <dt>Erledigung bis</dt>
              <dd className={data.slaState === 'overdue' ? 'badge danger' : undefined}>
                {data.due_at ? formatDateTime(data.due_at) : '–'}
              </dd>

              <dt>Erste Reaktion</dt>
              <dd>{data.first_response_at ? formatDateTime(data.first_response_at) : '–'}</dd>

              <dt>Erledigt am</dt>
              <dd>{data.completed_at ? formatDateTime(data.completed_at) : '–'}</dd>

              <dt>Geplante Dauer</dt>
              <dd>{formatDuration(data.estimated_minutes)}</dd>
            </dl>
          </Card>

          <Card title="Kosten" subtitle="Grundlage für die Weiterberechnung">
            <dl className="definition-list">
              <dt>Arbeitszeit</dt>
              <dd>{formatDuration(data.costs?.laborMinutes)}</dd>
              <dt>Personalkosten</dt>
              <dd>{formatCurrency(data.costs?.laborCost)}</dd>
              <dt>Materialkosten</dt>
              <dd>{formatCurrency(data.costs?.materialCost)}</dd>
              <dt>
                <strong>Gesamt</strong>
              </dt>
              <dd>
                <strong>{formatCurrency(data.costs?.totalCost)}</strong>
              </dd>
            </dl>
          </Card>

          {['done', 'closed'].includes(data.status) && <ReportCard taskId={data.id} taskNo={data.task_no} />}

          {data.reporter_id === user?.id && data.status === 'done' && !data.satisfaction_score && (
            <Card title="Ihre Rückmeldung">
              <p className="small muted">Wie zufrieden sind Sie mit der Erledigung?</p>
              <div className="row">
                {[1, 2, 3, 4, 5].map((score) => (
                  <button
                    key={score}
                    className="btn sm"
                    onClick={async () => {
                      await api.post(`/api/tasks/${id}/feedback`, { score });
                      notify('Vielen Dank für Ihre Rückmeldung!', 'success');
                      task.reload();
                    }}
                  >
                    {score}
                  </button>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {statusModal && (
        <StatusModal
          status={statusModal}
          label={statusLabels[statusModal] ?? statusModal}
          onClose={() => setStatusModal(null)}
          onConfirm={(commentText) => changeStatus(statusModal, commentText)}
        />
      )}

      {assignModal && (
        <AssignModal taskId={Number(id)} onClose={() => setAssignModal(false)} onDone={() => { setAssignModal(false); task.reload(); }} />
      )}

      {materialModal && (
        <MaterialModal taskId={Number(id)} onClose={() => setMaterialModal(false)} onDone={() => { setMaterialModal(false); task.reload(); }} />
      )}

      {orderModal && (
        <TaskOrderModal taskId={Number(id)} onClose={() => setOrderModal(false)} onDone={() => { setOrderModal(false); orders.reload(); }} />
      )}
    </div>
  );
}

function StatusModal({
  status,
  label,
  onClose,
  onConfirm,
}: {
  status: string;
  label: string;
  onClose: () => void;
  onConfirm: (comment?: string) => void;
}) {
  const [comment, setComment] = useState('');
  const requiresComment = ['rejected', 'cancelled', 'waiting_material', 'waiting_feedback'].includes(status);

  return (
    <Modal
      title={`Status ändern: ${label}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={requiresComment && !comment.trim()}
            onClick={() => onConfirm(comment || undefined)}
          >
            Bestätigen
          </button>
        </>
      }
    >
      <Field
        label="Kommentar"
        hint={requiresComment ? 'Für diesen Statuswechsel ist eine Begründung erforderlich.' : 'Optional – wird im Verlauf protokolliert.'}
        required={requiresComment}
      >
        <textarea value={comment} onChange={(event) => setComment(event.target.value)} autoFocus />
      </Field>
    </Modal>
  );
}

function AssignModal({ taskId, onClose, onDone }: { taskId: number; onClose: () => void; onDone: () => void }) {
  const suggestions = useAsync(() => api.get(`/api/tasks/${taskId}/assignment-suggestions`), [taskId]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const assign = async (assigneeId: number | null) => {
    setBusy(true);
    try {
      await api.post(`/api/tasks/${taskId}/assign`, { assigneeId });
      onDone();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Auftrag zuweisen"
      onClose={onClose}
      size="wide"
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn" onClick={() => assign(null)} disabled={busy}>
            Zuweisung aufheben
          </button>
        </>
      }
    >
      <ErrorMessage error={error} />
      <p className="small muted">
        Vorschläge berücksichtigen Qualifikation, Objektzuständigkeit, aktuelle Auslastung und Abwesenheiten.
      </p>
      {suggestions.loading ? (
        <Loading />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Mitarbeiter</th>
                <th>Bewertung</th>
                <th>Begründung</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(suggestions.data ?? []).map((entry: any) => (
                <tr key={entry.userId}>
                  <td>
                    <strong>{entry.name}</strong>
                    <div className="small subtle">{entry.jobTitle}</div>
                  </td>
                  <td>
                    <Badge variant={entry.available ? (entry.score > 40 ? 'success' : '') : 'danger'}>
                      {entry.available ? `${entry.score} Punkte` : 'Abwesend'}
                    </Badge>
                  </td>
                  <td className="small subtle">{entry.reasons.join(' · ')}</td>
                  <td className="actions">
                    <button className="btn primary sm" disabled={busy} onClick={() => assign(entry.userId)}>
                      Zuweisen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

function MaterialModal({ taskId, onClose, onDone }: { taskId: number; onClose: () => void; onDone: () => void }) {
  const articles = useAsync(() => api.get('/api/material/articles'), []);
  const warehouses = useAsync(() => api.get('/api/material/warehouses'), []);
  const [articleId, setArticleId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [isReturn, setIsReturn] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const save = async () => {
    try {
      await api.post('/api/material/bookings', {
        taskId,
        articleId: Number(articleId),
        warehouseId: warehouseId ? Number(warehouseId) : undefined,
        quantity: Number(quantity),
        isReturn,
      });
      onDone();
    } catch (caught) {
      setError(caught);
    }
  };

  return (
    <Modal
      title="Material buchen"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn primary" onClick={save} disabled={!articleId || Number(quantity) <= 0}>
            Buchen
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <Field label="Artikel" required>
          <SearchSelect
            value={String(articleId ?? '')}
            onChange={(wert) => setArticleId(wert)}
            options={(articles.data ?? []).map((article: any) => ({ value: String(article.id), label: `${article.article_no} · ${article.name} (Bestand ${article.total_stock} ${article.unit})` }))}
          />
        </Field>
        <Field label="Lagerort" hint="Leer lassen für das eigene Fahrzeuglager.">
          <SearchSelect
            value={String(warehouseId ?? '')}
            onChange={(wert) => setWarehouseId(wert)}
            options={(warehouses.data ?? []).map((warehouse: any) => ({ value: String(warehouse.id), label: String(warehouse.name) }))}
            placeholder="Automatisch"
          />
        </Field>
        <Field label="Menge" required>
          <input type="number" min="0.01" step="0.01" value={quantity} onChange={(event) => setQuantity(event.target.value)} />
        </Field>
        <Toggle checked={isReturn} onChange={setIsReturn} label="Rückgabe (nicht verbrauchtes Material zurückbuchen)" />
      </div>
    </Modal>
  );
}

function TaskOrderModal({ taskId, onClose, onDone }: { taskId: number; onClose: () => void; onDone: () => void }) {
  const suppliers = useAsync(() => api.get('/api/procurement/suppliers'), []);
  const articles = useAsync(() => api.get('/api/material/articles'), []);
  const [supplierId, setSupplierId] = useState('');
  const [note, setNote] = useState('');
  const [items, setItems] = useState<{ articleId: string; quantity: string }[]>([{ articleId: '', quantity: '1' }]);
  const [error, setError] = useState<unknown>(null);

  const total = items.reduce((sum, item) => {
    const article = (articles.data ?? []).find((entry: any) => String(entry.id) === item.articleId);
    return sum + (article ? article.purchase_price * Number(item.quantity || 0) : 0);
  }, 0);

  return (
    <Modal
      title="Materialanforderung stellen"
      onClose={onClose}
      size="wide"
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={!items.some((item) => item.articleId)}
            onClick={async () => {
              try {
                await api.post('/api/procurement/orders', {
                  taskId,
                  supplierId: supplierId ? Number(supplierId) : null,
                  note: note || undefined,
                  items: items
                    .filter((item) => item.articleId)
                    .map((item) => ({ articleId: Number(item.articleId), quantity: Number(item.quantity) })),
                });
                onDone();
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Anfordern ({formatCurrency(total)})
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <Field label="Lieferant">
          <SearchSelect
            value={String(supplierId ?? '')}
            onChange={(wert) => setSupplierId(wert)}
            options={(suppliers.data ?? []).map((supplier: any) => ({ value: String(supplier.id), label: String(supplier.name) }))}
          />
        </Field>

        <div className="stack" style={{ gap: 8 }}>
          <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)' }}>Positionen</label>
          {items.map((item, index) => (
            <div className="row" key={index} style={{ gap: 8, flexWrap: 'nowrap' }}>
              <SearchSelect
                value={String(item.articleId ?? '')}
                onChange={(wert) => setItems((current) => current.map((entry, i) => (i === index ? { ...entry, articleId: wert } : entry)))}
                options={(articles.data ?? []).map((article: any) => ({ value: String(article.id), label: `${article.article_no} · ${article.name}` }))}
                placeholder="Artikel wählen …"
                style={{ flex: 1 }}
              />
              <input
                type="number"
                min="1"
                value={item.quantity}
                onChange={(event) =>
                  setItems((current) => current.map((entry, i) => (i === index ? { ...entry, quantity: event.target.value } : entry)))
                }
                style={{ width: 100 }}
              />
              <button className="btn ghost sm" onClick={() => setItems((current) => current.filter((_, i) => i !== index))}>
                ×
              </button>
            </div>
          ))}
          <button className="btn sm" onClick={() => setItems((current) => [...current, { articleId: '', quantity: '1' }])}>
            Position hinzufügen
          </button>
        </div>

        <Field label="Bemerkung">
          <input value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

function approvalLabel(status: string): string {
  return { open: 'Offen', approved: 'Freigegeben', rejected: 'Abgelehnt' }[status] ?? status;
}

/**
 * Auftragsbericht am erledigten Auftrag.
 *
 * Der Bericht entsteht beim Abschluss von selbst; hier lässt er sich ansehen,
 * herunterladen und – etwa nach einem nachgereichten Foto – neu erzeugen.
 * Angezeigt wird auch, wer ihn bekäme: Eine Empfängerliste, die man erst nach
 * dem Versand sieht, hilft niemandem.
 */
function ReportCard({ taskId, taskNo }: { taskId: number; taskNo: string }) {
  const { notify } = useToast();
  const reports = useAsync(() => api.get(`/api/tasks/${taskId}/reports`), [taskId]);
  const recipients = useAsync(() => api.get(`/api/tasks/${taskId}/report/recipients`).catch(() => null), [taskId]);
  const [busy, setBusy] = useState(false);

  const open = async (reportId?: number) => {
    // Der Endpunkt verlangt den Token im Header – ein einfacher Link käme
    // unangemeldet an. Also laden und als Objekt-URL öffnen.
    const url = await fetchImageUrl(
      `/api/tasks/${taskId}/report.pdf${reportId ? `?reportId=${reportId}` : ''}`,
    );
    if (!url) {
      notify('Der Bericht konnte nicht geladen werden.', 'error');
      return;
    }
    window.open(url, '_blank');
    // Erst freigeben, wenn der neue Tab die Datei geholt hat.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const list = reports.data ?? [];

  return (
    <Card title="Auftragsbericht" subtitle={`PDF zu ${taskNo}`}>
      <div className="stack" style={{ gap: 10 }}>
        {list.length === 0 ? (
          <span className="small muted">Es wurde noch kein Bericht erzeugt.</span>
        ) : (
          <div className="stack" style={{ gap: 4 }}>
            {list.slice(0, 3).map((report: any) => (
              <div key={report.id} className="row between">
                <button className="btn sm ghost" onClick={() => open(report.id)}>
                  <IconDownload width={13} height={13} /> {formatDateTime(report.created_at)}
                </button>
                <span className="small subtle">{Math.round(report.size_bytes / 1024)} KB</span>
              </div>
            ))}
          </div>
        )}

        <div className="row" style={{ gap: 8 }}>
          <button className="btn sm primary" onClick={() => open()}>
            <IconDownload width={13} height={13} /> Bericht öffnen
          </button>
          <button
            className="btn sm"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const result = await api.post(`/api/tasks/${taskId}/report`, { sendMail: true });
                notify(
                  result.mailId
                    ? `Bericht erzeugt und an ${result.recipients.length} Empfänger eingereiht.`
                    : `Bericht erzeugt. ${result.reason ?? ''}`.trim(),
                  result.mailId ? 'success' : 'info',
                );
                reports.reload();
              } catch (error) {
                notify(error instanceof Error ? error.message : 'Der Versand ist fehlgeschlagen.', 'error');
              } finally {
                setBusy(false);
              }
            }}
          >
            <IconMail width={13} height={13} /> Neu erzeugen & senden
          </button>
        </div>

        {recipients.data && (
          <div className="small subtle">
            {recipients.data.recipients.length === 0 ? (
              <>Kein Empfänger konfiguriert – der Bericht wird nur abgelegt.</>
            ) : (
              <>
                Geht an: {recipients.data.recipients.map((entry: any) => entry.label).join(', ')}
                {!recipients.data.enabled && ' (automatischer Versand ist ausgeschaltet)'}
              </>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
